import * as PIXI from 'pixi.js';
import { gsap } from 'gsap';
import { Spine } from 'pixi-spine';
import { PixiPlugin } from 'gsap/PixiPlugin';
import { isMobile } from '@pascal/pascal-core';

import {
  diceSourceNames,
  magicDiceAnimationNames,
  MagicDiceLayers,
  magicDiceMobileAnimationSources,
  magicDiceWebAnimationSources,
} from '../core/configs/constants';
import {
  MagicDiceCanvasMobileConfigs,
  MagicDiceCanvasWebConfigs,
  mobileConfigs,
  webConfigs,
} from '../core/configs/applicationConfig';
import { debounce } from '../helpers/debounce';
import { PixiBase } from '../services/pixi/pixiBase';
import { chestAnimations } from '../services/ChestAnimations';
import { dancerAnimations } from '../services/DancerAnimations';
import { PixiGameDrawer } from '../services/pixi/pixiGameDrawer';
import { pixiResourceHelper } from '../helpers/pixiResourceHelper';

class GameDrawer extends PixiGameDrawer {
  constructor(props) {
    super(props);
    this.configs = isMobile() ? mobileConfigs : webConfigs;
    this.isDraw = false;
    this.animations = {};
    this.containers = {
      [MagicDiceLayers.background]: PixiBase.createContainer(MagicDiceLayers.background),
      [MagicDiceLayers.winNumber]: PixiBase.createContainer(MagicDiceLayers.winNumber),
      [MagicDiceLayers.dices]: PixiBase.createContainer(MagicDiceLayers.dices),
      [MagicDiceLayers.dancerGirl]: PixiBase.createContainer(MagicDiceLayers.dancerGirl),
      [MagicDiceLayers.chest]: PixiBase.createContainer(MagicDiceLayers.chest),
      *[Symbol.iterator]() {
        yield this[MagicDiceLayers.background];
        yield this[MagicDiceLayers.winNumber];
        yield this[MagicDiceLayers.dices];
        yield this[MagicDiceLayers.dancerGirl];
        yield this[MagicDiceLayers.chest];
      },
    };
  }

  execute(root) {
    return new Promise((resolve) => {
      super
        .loadResource({
          resource: !isMobile() ? magicDiceWebAnimationSources : magicDiceMobileAnimationSources,
          loader: PixiBase.createLoader(),
        })
        .then(() => {
          root.appendChild(this.pixiApplication.view);
          super.setupContainers(this.containers);
          this.setupPixiPlugins();
          this.setStaticAnimations();
          resolve(true);
        });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  setupPixiPlugins() {
    gsap.registerPlugin(PixiPlugin);
    PixiPlugin.registerPIXI(PIXI);
  }

  // eslint-disable-next-line class-methods-use-this
  getSlot({ texture, name }) {
    const index = texture.spineData.slots.findIndex((slotData) => slotData.name === name);
    return texture.children[index].children[0];
  }

  registerClick(texture, onClick) {
    return texture.on('pointerdown', onClick);
  }

  // eslint-disable-next-line class-methods-use-this
  setupButtonMode(texture, { interactive = true, buttonMode = true } = {}) {
    // eslint-disable-next-line no-param-reassign
    texture.interactive = interactive;
    // eslint-disable-next-line no-param-reassign
    texture.buttonMode = buttonMode;
  }

  // eslint-disable-next-line class-methods-use-this
  createGameBackground() {
    if (isMobile()) {
      this.backgroundSprite = PixiBase.createSprite(pixiResourceHelper.get(magicDiceAnimationNames.background));
      PixiBase.setPosition(this.backgroundSprite, this.configs.bgPosition);
      this.backgroundSprite.scale.set(1.7);
      this.backgroundSprite.anchor.set(0.5);
      this.containers[MagicDiceLayers.background].addChild(this.backgroundSprite);
      return this.backgroundSprite;
    }
  }

  createChestAnimation() {
    this.chestSpine = PixiBase.createSpine(pixiResourceHelper.get(magicDiceAnimationNames.chest).spineData);
    this.chestSpine.state.timeScale = 1.9;
    PixiBase.setPosition(this.chestSpine, this.configs.chestAnimationPosition);
    this.chestSpine.scale.set(isMobile() ? 3.2 : 1);
    chestAnimations.setupAnimations(this.chestSpine);
    chestAnimations.startStaticAnimation(false);
    this.containers[MagicDiceLayers.chest].addChild(this.chestSpine);
  }

  createDancerGirlAnimation() {
    this.dancerGirlSpine = PixiBase.createSpine(pixiResourceHelper.get(magicDiceAnimationNames.dancerGirl).spineData);
    this.dancerGirlSpine.state.timeScale = 2;
    PixiBase.setPosition(this.dancerGirlSpine, this.configs.dancerAnimationPosition);
    this.dancerGirlSpine.scale.set(isMobile() ? 3.2 : 1);
    const dancerChestSlot = this.getSlot({ texture: this.dancerGirlSpine, name: 'chest' });
    dancerAnimations.setupAnimations(this.dancerGirlSpine);
    this.setupButtonMode(dancerChestSlot);
    if (isMobile()) {
      const onClick = () =>
        debounce(
          () => {
            dancerAnimations.startPushAnimation();
          },
          500,
          true
        );
      this.registerClick(dancerChestSlot, onClick());
    }
    dancerAnimations.startBlinkAnimation(0, true);
    this.containers[MagicDiceLayers.dancerGirl].addChild(this.dancerGirlSpine);
  }

  setWinNumberContainer(winNumber) {
    this.winNumber = PixiBase.createText(winNumber, this.configs.winNumberStyle);
    this.winNumber.anchor.set(0.5);
    PixiBase.setPosition(this.winNumber, this.configs.winNumberStaticPosition);
    this.containers[MagicDiceLayers.winNumber].addChild(this.winNumber);
    return this.winNumber;
  }

  setDices(dice, index) {
    this.dicesSprite = PixiBase.createSprite(
      pixiResourceHelper.get(pixiResourceHelper.getRandom(diceSourceNames[dice - 1]))
    );
    this.dicesSprite.alpha = 0;
    PixiBase.setPosition(this.dicesSprite, this.configs.dicesFirstPositions[index]);
    this.dicesSprite.scale.set(0.7);
    this.dicesSprite.anchor.set(0.5);
    this.containers[MagicDiceLayers.dices].addChild(this.dicesSprite);
    return this.containers[MagicDiceLayers.dices];
  }

  setStaticAnimations() {
    if (this.isDraw) return;
    this.createGameBackground();
    this.createChestAnimation();
    this.createDancerGirlAnimation();
    this.isDraw = true;
  }

  destroy() {
    Object.keys(MagicDiceLayers).forEach((name) => {
      this.containers[name].removeChildren();
    });
  }
}

export const gameDrawer = new GameDrawer({
  Spine,
  PIXI,
  config: isMobile() ? MagicDiceCanvasMobileConfigs : MagicDiceCanvasWebConfigs,
});
